import { useLazyQuery } from "@apollo/client";
import { createStyles, Grid, makeStyles, Typography } from "@material-ui/core";
import { theme } from "config/theme";
import useText from "constants/text";
import { ITourTemplateAxe, NewRequestQuoteContext } from "contexts/RequestQuoteContext";
import { GET_REQUEST_BY_CLIENT } from "domains/Request/graphql/queries";
import React from "react";
import { uuid } from "utils";
import { AxeReccurence } from "./AxeReccurence";
import { AxeVehicleTypeDialog } from "./AxeVehicleTypeDialog";
import TemplateAxe from "./TemplateAxe";

interface Props {}
export const useStyles = makeStyles(() =>
  createStyles({
    addAxe: {
      borderRadius: 2,
      border: "1px solid #399ebf ",
      backgroundColor: "#399ebf",
      color: "#fff",
      fontSize: 20,
      alignItems: "center",
      justifyContent: "center",
      display: "flex",
      width: 32,
      height: 32,
      cursor: "pointer",
    },
    button: {
      borderRadius: 2,
      borderWidth: 1,
      borderColor: "#399ebf",
      backgroundColor: "#399ebf",
      color: "white",
      marginLeft: 20,
      fontSize: 20,
      width: 30,
      height: 30,
    },
    addButton: {
      borderRadius: 2,
      borderWidth: 1,
      borderColor: "#399ebf",
      backgroundColor: "#399ebf",
      color: "white",
      fontSize: 20,
      width: 30,
      height: 30,
      [theme.breakpoints.down("xs")]: {
        marginLeft: 15,
      },
    },
    gridContainer: {
      [theme.breakpoints.down("xs")]: {
        marginLeft: 15,
      },
      padding: "11px 9px",
    },
    buttonTitle: {
      fontWeight: "bold",
      fontSize: 12,
      marginLeft: "10px",
    },
  })
);

export const TourAxes = ({ isEdited }: { isEdited: boolean }) => {
  const classes = useStyles();
  const { text } = useText();
  const [showVehicle, setShowVehicle] = React.useState(false);
  const [showReccurence, setShowReccurence] = React.useState(false);
  const [activeAxe, setActiveAxe] = React.useState(null);
  const { requestQuoteTour, requestQuoteDetails, setRequestQuoteTour } = React.useContext(NewRequestQuoteContext);

  const [fetchRequest, { data, loading }] = useLazyQuery(GET_REQUEST_BY_CLIENT, {
    variables: {
      params: {
        id: requestQuoteTour?._id,
      },
    },
  });

  const TemplateAxes = React.useMemo(() => {
    return requestQuoteTour?.templateAxes ? requestQuoteTour?.templateAxes : [];
  }, [requestQuoteTour?.templateAxes]);

  React.useEffect(() => {
    if (loading === false && data?.results?.data) {
      setRequestQuoteTour(data?.results?.data);
    }
  }, [data]);

  React.useEffect(() => {
    if (requestQuoteTour?._id) {
      fetchRequest();
    }
  }, [requestQuoteTour]);

  const defaultTemplateAxe: ITourTemplateAxe = {
    uid: uuid(),
    name: "",
    templateStops: [
      {
        address: null,
      },
      {
        address: null,
      },
    ],
    vehicles: [],
    price: 0,
  };

  const addNewAxe = () => {
    if (TemplateAxes?.every((templateAxe) => templateAxe.name)) {
      setRequestQuoteTour({ ...requestQuoteTour, templateAxes: [...TemplateAxes, defaultTemplateAxe] });
    }
  };

  React.useEffect(() => {
    if (!TemplateAxes?.length) {
      addNewAxe();
    }
  }, [TemplateAxes]);

  const deleteAxe = (templateAxeToDelete: ITourTemplateAxe) => {
    if (TemplateAxes?.length > 1) {
      const filtered = TemplateAxes?.filter((tAxe) => tAxe?.uid !== templateAxeToDelete?.uid);
      setRequestQuoteTour({ ...requestQuoteTour, templateAxes: filtered });
    }
  };

  const handleVehicleType = (axe: ITourTemplateAxe) => {
    setActiveAxe(axe);
    setShowVehicle(true);
  };

  const handleReccurrence = (axe: ITourTemplateAxe) => {
    setActiveAxe(axe);
    setShowReccurence(true);
  };
  const handleClose = () => {
    setShowVehicle(false);
    setShowReccurence(false);
    setActiveAxe(null);
  };

  const handleCloseReccurence = (tAxe: ITourTemplateAxe) => {
    const templateAxes = [...TemplateAxes];
    const indexTemplateAxe = templateAxes.findIndex((el: any) => el?.uid === tAxe?.uid);
    templateAxes[indexTemplateAxe] = tAxe;
    setRequestQuoteTour({ ...requestQuoteTour, templateAxes });
    setShowReccurence(false);
    setActiveAxe(null);
  };

  const renderAxes = () =>
    TemplateAxes.map((tAxe, index) => (
      <TemplateAxe
        key={index}
        index={index}
        onVehicleButtonClicked={handleVehicleType}
        onAxeReccurenceButtonClicked={handleReccurrence}
        onDelete={deleteAxe}
        templateAxe={tAxe}
      />
    ));

  return (
    <>
      {renderAxes()}
      <Grid container xs={12} alignItems="center" spacing={2} className={classes.gridContainer}>
        <div onClick={addNewAxe} className={classes.addAxe}>
          +
        </div>
        <Typography color="primary" className={classes.buttonTitle}>
          {text.requestQuote.add_another_axe_button}
        </Typography>
      </Grid>
      <AxeReccurence onClose={handleCloseReccurence} open={showReccurence} templateAxe={activeAxe} />
      <AxeVehicleTypeDialog onClose={handleClose} open={showVehicle} templateAxe={activeAxe} />
    </>
  );
};
